import axios from 'axios';
import { clearAuthData, getCookie } from '../utils/authUtils';
import { captureException, addSentryBreadcrumb } from '../utils/sentry';
import { useAppStore } from '../store/useAppStore';
import { getRedirectUrl } from '../utils/helpers';

let isRedirectingToLogin = false;

const redirectToLogin = () => {
  if (typeof window === 'undefined' || isRedirectingToLogin) return;

  isRedirectingToLogin = true;
  clearAuthData();
  window.location.replace(getRedirectUrl('/login'));
};

class MarketChefAPI {
  constructor() {
    // Use environment variable for API base URL
    this.baseURL = import.meta.env.VITE_API_BASE_URL;
    this.accessKey = getCookie('auth_token');
    this.instagramClientId = '1141120864610972';
    this.facebookAppId = '2779803235696822';
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.accessKey}`,
      },
    });

    // Request interceptor to add Bearer token
    this.client.interceptors.request.use((config) => {
      if (this.accessKey) {
        config.headers.Authorization = `Bearer ${this.accessKey}`;
      }
      return config;
    });

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response.data,
      (error) => {
        if (error.response) {
          // If the error is 401, redirect to the login page
          if (error.response.status === 401) {
            useAppStore.getState().setIsAuthenticated(false);
            redirectToLogin();
          }

          // Send error to Sentry for tracking
          const errorMessage = error.response.data?.message || error.message;
          captureException(error, {
            api: {
              method: error.config?.method?.toUpperCase(),
              url: error.config?.url,
              status: error.response?.status,
              statusText: error.response?.statusText,
            },
          });

          // Add breadcrumb for debugging
          addSentryBreadcrumb(
            `API Error: ${error.response.status}`,
            {
              method: error.config?.method?.toUpperCase(),
              url: error.config?.url,
              message: errorMessage,
            },
            'error'
          );
        } else if (error.request) {
          // Request made but no response
          captureException(error, {
            api: {
              type: 'no_response',
              method: error.config?.method?.toUpperCase(),
              url: error.config?.url,
            },
          });
        }
        return Promise.reject(error);
      }
    );
  }

  setAccessKey(accessKey) {
    this.accessKey = accessKey;
  }

  // Generic request method to match PHP client
  async request(method, endpoint, body = null) {
    const config = {
      method: method.toUpperCase(),
      url: endpoint,
    };

    if (body !== null) {
      config.data = body;
    }

    return this.client.request(config);
  }

  // ==================== API METHODS ====================

  // Authentication
  async activateAccessKey(accessKey) {
    return this.request('POST', '/v1/auth/key/activate', { access_key: accessKey });
  }

  async getUserDetails() {
    const result = await this.request('GET', '/v1/user');
    return result;
  }

  async getUserIdentity() {
    const result = await this.request('GET', '/v1/user/identity');
    return result;
  }

  async getUserSubscription() {
    const result = await this.request('GET', '/v1/user/subscription');
    return result;
  }

  async getUserCredits() {
    const result = await this.request('GET', '/v1/credits');
    return result;
  }

  async getCampaigns() {
    const result = await this.request('GET', '/v1/campaigns');
    return result;
  }

  async getCampaignPosts(campaignId) {
    const result = await this.request('GET', `/v1/campaigns/${campaignId}/posts`);
    return result;
  }

  // Onboarding
  async createProfileFromWebsite(websiteUrl) {
    const result = await this.request('POST', '/v1/profile/from-website', { website: websiteUrl });
    return result;
  }

  async createProfileFromText({ businessName, industry, description }) {
    const result = await this.request('POST', '/v1/profile/from-text', {
      business_name: businessName,
      industry,
      description,
    });
    return result;
  }

  async fetchProfileStyle() {
    return this.request('GET', '/v1/profile/style');
  }

  async updateProfileStyle(data) {
    return this.request('PUT', '/v1/profile/style', data);
  }

  async hasCreatedProfile() {
    const profile = await this.checkProfileReady();
    return profile?.status === 'created';
  }

  // Profile Management
  async checkProfileReady() {
    const result = await this.request('GET', '/v1/profile');
    return result;
  }

  async checkProfileSummaryReady() {
    if (!(await this.hasCreatedProfile())) {
      return null;
    }

    const result = await this.request('GET', '/v1/profile/summary');
    return result;
  }

  async checkOfferingsReady() {
    if (!(await this.hasCreatedProfile())) {
      return [];
    }

    const result = await this.request('GET', '/v1/profile/offering');
    return result;
  }

  async deleteProfileOffering(offeringId) {
    return this.request('DELETE', `/v1/profile/offering/${offeringId}`);
  }
  async updateProfileOffering(offeringId, offeringData) {
    return this.request('PUT', `/v1/profile/offering/${offeringId}`, offeringData);
  }

  async createProfileOffering(offeringData) {
    return this.request('POST', '/v1/profile/offering', offeringData);
  }

  // Upload a new photo against an existing offering. Returns the created
  // `intelligence.Media` (id, offering_id, media_url, alt_text, ...) which
  // the caller can slot into `selectedPhotos` for the agent flow.
  async uploadOfferingMedia(offeringId, file, altText = '') {
    const formData = new FormData();
    formData.append('file', file);
    if (altText) formData.append('alt_text', altText);

    return this.client.request({
      method: 'POST',
      url: `/v1/profile/offering/${offeringId}/media`,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async uploadProfileLogo(file) {
    const formData = new FormData();
    formData.append('file', file);

    return this.client.request({
      method: 'POST',
      url: '/v1/profile/logo',
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async checkPostsReady() {
    const result = await this.request('GET', '/v1/posts');
    return result;
  }

  async fetchProfile() {
    return this.request('GET', '/v1/profile');
  }

  async fetchProfileSummary() {
    return this.checkProfileSummaryReady();
  }

  async fetchProfileOfferings() {
    return this.checkOfferingsReady();
  }

  async updateProfileSummary(summaryData) {
    return this.request('PUT', '/v1/profile/summary', summaryData);
  }

  // Posts Management
  async fetchPosts(profileId) {
    return this.request('GET', `/v1/posts/${profileId}`);
  }

  async updatePost(postId, postData) {
    return this.request('PUT', `/v1/posts/${postId}`, postData);
  }

  async regeneratePost(postUid, userInput) {
    return this.request('POST', `/v1/posts/${postUid}/regenerate`, { userInput });
  }

  async getPostDetails(postId) {
    return this.request('GET', `/v1/posts/${postId}`);
  }

  async approvePost(postId, socialAccountsPlatforms = [], timezone = '') {
    const data = {
      socialAccountsPlatforms,
      timezone,
    };

    return this.request('POST', `/v1/posts/${postId}/approve`, data);
  }

  async cancelPost(postId, socialAccountsPlatforms = [], timezone = '') {
    const data = {
      socialAccountsPlatforms,
      timezone,
    };
    return this.request('POST', `/v1/posts/${postId}/approve`, data);
  }

  // Social Media
  async fetchSocialAccounts() {
    return this.request('GET', '/v1/social/accounts');
  }

  async saveNotificationToken(notificationToken) {
    return this.request('POST', '/v1/user/notification/token', notificationToken);
  }

  async sendPushNotification(notificationData) {
    return this.request('POST', '/v1/notification/send', notificationData);
  }

  // async fetchInstagramInsights() {
  //   return this.request('GET', '/v1/social/instagram/insights');
  // }

  // Profile editing
  async editProfile(profileId, profileData) {
    return this.request('POST', `/profile/${profileId}/edit`, profileData);
  }

  //https://www.instagram.com/oauth/authorize?force_reauth=true&client_id=1141120864610972&redirect_uri=https://api.postreams.com/v1/social/instagram/callback&response_type=code&scope=instagram_business_basic%2Cinstagram_business_manage_messages%2Cinstagram_business_manage_comments%2Cinstagram_business_content_publish%2Cinstagram_business_manage_insights
  async connectInstagramAccount() {
    const redirectUri = `${this.baseURL}/v1/social/instagram/callback`;
    const scopes = encodeURIComponent(
      'instagram_business_basic,instagram_business_content_publish'
    );

    const stateObj = {
      access_key: getCookie('auth_token'),
      intr: `${window.location.protocol}//${window.location.host}/socials`,
    };
    const state = encodeURIComponent(JSON.stringify(stateObj));
    const authUrl = `https://www.instagram.com/oauth/authorize?force_reauth=true&client_id=${this.instagramClientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scopes}&state=${state}`;

    localStorage.setItem('instagram_oauth_state', state);

    window.location.href = authUrl;
  }

  async connectFacebookAccount() {
    const backendRedirect = `${this.baseURL}/v1/social/facebook/callback`;
    const scopes = [
      'business_management',
      'pages_show_list',
      'pages_manage_posts',
      'instagram_basic',
      'instagram_content_publish',
    ].join(',');

    const stateObj = {
      access_key: getCookie('auth_token'),
      intr: `${window.location.protocol}//${window.location.host}/socials`,
    };

    const state = encodeURIComponent(JSON.stringify(stateObj));
    const authUrl = `https://www.facebook.com/v23.0/dialog/oauth?client_id=${this.facebookAppId}&redirect_uri=${backendRedirect}&scope=${scopes}&response_type=code&state=${state}`;

    window.location.href = authUrl;
  }

  // OAuth Callback Handlers
  async handleInstagramCallback(code, state) {
    return this.request('POST', '/v1/social/instagram/callback', {
      code,
      state,
    });
  }

  async handleFacebookCallback(code, state) {
    return this.request('POST', '/v1/social/facebook/callback', {
      code,
      state,
    });
  }

  // Disconnect accounts
  async disconnectAccount(accountId) {
    return this.request('DELETE', `/v1/social-account/${accountId}`);
  }

  async disconnectSocial(platform) {
    return this.request('DELETE', `/v1/social/${platform}/disconnect`);
  }

  async sendFeedback(message, post_id) {
    return this.request('POST', '/v1/user/feedback', { message, post_id });
  }

  // Up-front batch ideation — replaces the agent's bootstrap turn.
  // SSE-streamed: invokes onIdea once per completed preview, onError per
  // failed preview, and onDone when the stream closes. Returns the
  // AbortController so callers can cancel mid-stream.
  async agentIdeate(brief, { onIdea, onError, onDone } = {}) {
    const controller = new AbortController();

    try {
      const response = await fetch(`${this.baseURL}/v1/agent/ideate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.accessKey}`,
        },
        body: JSON.stringify(brief),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Ideate failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const evt = JSON.parse(line.slice(6));
            if (evt.type === 'idea' && evt.idea) {
              onIdea?.(evt.idea);
            } else if (evt.type === 'error') {
              onError?.(evt.message || 'unknown error');
            } else if (evt.type === 'done') {
              onDone?.();
            }
          } catch {
            // skip malformed SSE frames
          }
        }
      }
      onDone?.();
    } catch (err) {
      if (err.name !== 'AbortError') {
        onError?.(err.message);
      }
    }

    return controller;
  }

  // Persist user-approved ideas as a campaign (no agent). `goal` is the
  // enriched campaign goal returned by /agent/campaign-plan; it ends up
  // on the campaigns.goal column for downstream consistency.
  async createCampaignFromIdeas(posts, goal = '') {
    return this.request('POST', '/v1/campaign/from-ideas', { posts, goal });
  }

  // Plan high-level campaign metadata. Returns `{ goal, photos }`. In
  // auto mode `photos` is an auto-selected mix so /agent/ideate produces
  // a balanced batch of ProductAd + AIGenAd posts; in manual mode it's
  // empty and the user picks per slot.
  async campaignPlan(brief) {
    return this.request('POST', '/v1/agent/campaign-plan', brief);
  }

  // Sync pre-create + async fill. Returns 202 with
  // { status, campaignId, postIds } so the client can immediately
  // redirect into a post-anchored loading UX. Ideation + Step Function
  // dispatch run in the background; the pre-created rows transition
  // from "draft" to "pending" as each post finishes generation.
  async autoCreateCampaign(brief) {
    return this.request('POST', '/v1/campaign/auto-create', brief);
  }

  // Agent Chat (SSE streaming).
  // The optional `ideas` param is passed only on the first chat call after
  // /ideate so the server can seed the new conversation with the ideas array.
  async agentChat(message, onDelta, onDone, onError, ideas = null, profileId = null) {
    const controller = new AbortController();

    try {
      const body = { message };
      if (profileId) body.profileId = profileId;
      if (ideas) body.ideas = ideas;
      const response = await fetch(`${this.baseURL}/v1/agent/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.accessKey}`,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Agent chat failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'delta') {
              onDelta?.(data.text);
            } else if (data.type === 'done') {
              onDone?.(data.usage);
            } else if (data.type === 'error') {
              onError?.(data.text);
            }
          } catch {
            // skip malformed SSE lines
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        onError?.(err.message);
      }
    }

    return controller;
  }

  async getOfferingsWithMedia() {
    return this.request('GET', '/v1/agent/offerings-with-media');
  }

  async agentReset() {
    return this.request('POST', '/v1/agent/reset');
  }

  // Returns pre-generated content ideas for the authenticated profile.
  // On first call the server generates and persists a batch of 10 via AI.
  // Response: { ideas: ProfileIdea[], batch: number }
  async getProfileIdeas() {
    return this.request('GET', '/v1/ideas');
  }

  // Triggers the server to generate a new batch of 10 ideas (batch N+1)
  // and returns only the newly created ideas.
  // Response: { ideas: ProfileIdea[], batch: number }
  async refreshProfileIdeas() {
    return this.request('POST', '/v1/ideas/refresh');
  }
}

export const api = new MarketChefAPI();
